(() => {
  'use strict';
  const init = (root = document) => {
    const menu = root.querySelector('.menu-toggle');
    if (menu && !menu.dataset.ready) {
      menu.dataset.ready = 'true';
      const panel = document.getElementById('mobile-menu');
      const close = () => { menu.setAttribute('aria-expanded', 'false'); menu.setAttribute('aria-label', 'Open navigation'); panel.hidden = true; };
      menu.addEventListener('click', () => { const open = menu.getAttribute('aria-expanded') !== 'true'; menu.setAttribute('aria-expanded', String(open)); menu.setAttribute('aria-label', open ? 'Close navigation' : 'Open navigation'); panel.hidden = !open; });
      panel.querySelectorAll('a').forEach(a => a.addEventListener('click', close));
      const desktopNavigation = window.matchMedia('(min-width: 901px)');
      desktopNavigation.addEventListener('change', event => { if (event.matches) close(); });
      document.addEventListener('keydown', e => { if (e.key === 'Escape' && !panel.hidden) { close(); menu.focus(); } });
    }
    root.querySelectorAll('[data-art]').forEach(button => {
      if (button.dataset.ready) return;
      button.dataset.ready = 'true';
      button.addEventListener('click', () => {
        const preview = document.getElementById('portrait-preview');
        preview.removeAttribute('srcset');
        preview.src = button.dataset.artSrc || button.querySelector('img').src;
        preview.alt = button.dataset.artAlt;
        document.querySelectorAll('[data-art]').forEach(b => b.setAttribute('aria-pressed', String(b === button)));
        // Gallery and Step 3 must describe the same portrait on the order.
        const option = Array.from(document.querySelectorAll('[name="properties[Character]"]')).find(input => input.dataset.characterArt === button.dataset.art);
        if (option && !option.checked) { option.checked = true; option.dispatchEvent(new Event('change', { bubbles: true })); }
      });
    });
    const form = root.querySelector('#portrait-form');
    if (!form || form.dataset.ready) return;
    form.dataset.ready = 'true';
    const fileInput = form.querySelector('#dog-photo');
    const error = form.querySelector('#photo-error');
    const uploadZone = form.querySelector('[data-upload-zone]');
    const photoList = form.querySelector('[data-photo-list]');
    const label = form.querySelector('[data-file-label]');
    const hint = form.querySelector('[data-upload-hint]');
    const photoStatus = form.querySelector('[data-photo-status]');
    const photos = [];
    let dragDepth = 0;
    let preparingPhotos = false;
    let heicDecoder;
    const fileKey = file => `${file.name}:${file.size}:${file.lastModified}`;
    const isHeic = file => /^image\/hei[cf](?:-sequence)?$/i.test(file.type) || (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) && /\.hei[cf]$/i.test(file.name));
    // A single picker feeds separate native file inputs, one Shopify property per image.
    // With JavaScript disabled, the original required input still accepts one photo.
    fileInput.multiple = true;
    fileInput.removeAttribute('name');
    const showPhotoError = message => {
      error.textContent = message;
      error.hidden = !message;
      fileInput.setAttribute('aria-invalid', String(Boolean(message)));
    };
    const fileError = file => {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) && !isHeic(file) && !(!file.type && /\.(jpe?g|png|webp)$/i.test(file.name))) return 'Please choose a JPG, PNG, WebP or iPhone HEIC photo.';
      if (file.size > 10 * 1024 * 1024) return 'Each photo must be under 10 MB. Please choose a smaller version.';
      if (!file.size) return 'This photo is empty. Please choose another file.';
      return '';
    };
    const timed = (promise, milliseconds) => new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Photo preparation timed out')), milliseconds);
      promise.then(value => { clearTimeout(timer); resolve(value); }, error => { clearTimeout(timer); reject(error); });
    });
    const jpegFromImage = async image => {
      const width = image.naturalWidth || image.width;
      const height = image.naturalHeight || image.height;
      if (!width || !height) throw new Error('Empty image');
      // A 4096px reference retains fine portrait detail without a huge mobile canvas.
      const scale = Math.min(1, 4096 / Math.max(width, height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);
      try {
        canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.95));
        if (!blob || blob.type !== 'image/jpeg' || blob.size > 10 * 1024 * 1024) throw new Error('Cannot prepare photo');
        return blob;
      } finally { canvas.width = 1; canvas.height = 1; }
    };
    const convertHeic = async file => {
      let jpeg;
      const url = URL.createObjectURL(file);
      const image = new Image();
      try {
        // Safari can decode many iPhone photos natively, avoiding the fallback download.
        image.src = url;
        await timed(image.decode(), 15000);
        jpeg = await jpegFromImage(image);
      } catch {
        // Pinned, self-hosted CSP build. Conversion stays on the customer's device.
        // Library source/licenses: heic-library-license.txt beside this script.
        heicDecoder ||= import(fileInput.dataset.heicDecoder).catch(error => { heicDecoder = null; throw error; });
        const { heicTo } = await heicDecoder;
        const bitmap = await heicTo({ blob: file, type: 'bitmap' });
        try { jpeg = await jpegFromImage(bitmap); } finally { bitmap.close(); }
      } finally { image.src = ''; URL.revokeObjectURL(url); }
      return new File([jpeg], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg', lastModified: file.lastModified });
    };
    const validatePhoto = () => {
      const unreadable = photos.find(photo => photo.unreadable);
      const message = unreadable ? 'We could not open one of your photos. Remove it and try a new JPG or PNG.' : '';
      fileInput.setCustomValidity(message);
      if (message) showPhotoError(message);
      return photos.length > 0 && !message;
    };
    const syncFiles = () => {
      const selection = new DataTransfer();
      photos.forEach(photo => selection.items.add(photo.file));
      fileInput.files = selection.files;
      form.querySelectorAll('[data-photo-file]').forEach(input => input.remove());
      photos.forEach((photo, index) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.hidden = true;
        input.name = `properties[Dog photo${index ? ' ' + (index + 1) : ''}]`;
        input.setAttribute('data-photo-file', '');
        const transfer = new DataTransfer();
        transfer.items.add(photo.file);
        input.files = transfer.files;
        form.append(input);
      });
      validatePhoto();
    };
    const renderPhotos = () => {
      photoList.replaceChildren();
      photos.forEach((photo, index) => {
        const item = document.createElement('li');
        item.className = 'selected-photo';
        const image = document.createElement('img');
        image.className = 'photo-preview';
        image.alt = `Selected dog photo ${index + 1}`;
        image.width = 100;
        image.height = 110;
        image.hidden = Boolean(photo.unreadable);
        image.addEventListener('error', () => {
          if (!photos.includes(photo)) return;
          photo.unreadable = true;
          image.hidden = true;
          validatePhoto();
        });
        image.src = photo.url;
        const filename = document.createElement('span');
        filename.className = 'photo-filename';
        filename.textContent = photo.file.name;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'remove-photo';
        remove.setAttribute('aria-label', `Remove photo ${index + 1}: ${photo.file.name}`);
        remove.textContent = '×';
        remove.disabled = preparingPhotos;
        remove.addEventListener('click', () => {
          photos.splice(photos.indexOf(photo), 1);
          URL.revokeObjectURL(photo.url);
          showPhotoError('');
          syncFiles();
          renderPhotos();
          photoStatus.textContent = photos.length ? `Photo removed. ${photos.length} of 3 photos added.` : 'Photo removed. Add at least one photo to continue.';
          fileInput.focus();
        });
        item.append(image, filename, remove);
        photoList.append(item);
      });
      photoList.hidden = !photos.length;
      uploadZone.classList.toggle('has-photos', Boolean(photos.length));
      label.textContent = photos.length ? `${photos.length} of 3 photos added` : 'Add 1–3 photos of your dog';
      hint.textContent = photos.length === 3 ? 'All set. Use × to remove a photo.' : photos.length ? 'Drop or choose more photos, or use × to remove one' : 'Drag photos here, or click to choose';
    };
    const setPreparing = value => {
      preparingPhotos = value;
      fileInput.disabled = value;
      uploadZone.setAttribute('aria-busy', String(value));
      photoStatus.classList.toggle('visually-hidden', !value);
      photoList.querySelectorAll('button').forEach(button => { button.disabled = value; });
      updateFormat();
    };
    const addPhotos = async files => {
      if (preparingPhotos) return;
      const incoming = Array.from(files || []);
      if (!incoming.length) { syncFiles(); return; } // Cancelling the picker keeps earlier photos.
      const seen = new Set(photos.map(photo => photo.sourceKey));
      const additions = incoming.filter(file => { const key = fileKey(file); if (seen.has(key)) return false; seen.add(key); return true; });
      const message = photos.length + additions.length > 3 ? 'You can add up to 3 photos. Remove one with × before adding another.' : additions.map(fileError).find(Boolean);
      if (message) { syncFiles(); showPhotoError(message); return; }
      const prepared = [];
      try {
        showPhotoError('');
        if (additions.some(isHeic)) setPreparing(true);
        for (const [index, original] of additions.entries()) {
          let file = original;
          if (isHeic(file)) {
            label.textContent = 'Preparing your iPhone photo…';
            photoStatus.textContent = `Preparing photo ${index + 1} of ${additions.length}. Please keep this page open.`;
            file = await timed(convertHeic(file), 60000);
          } else if (!file.type) {
            const extension = file.name.split('.').pop().toLowerCase();
            file = new File([file], file.name, { type: { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp' }[extension], lastModified: file.lastModified });
          }
          prepared.push({file, sourceKey: fileKey(original), unreadable: false});
        }
        prepared.forEach(photo => photos.push({...photo, url: URL.createObjectURL(photo.file)}));
        photoStatus.textContent = `${photos.length} of 3 photos added.`;
      } catch {
        showPhotoError('We could not prepare that iPhone photo. Please try another photo, or choose a JPG. Your other photos are still here.');
      } finally {
        setPreparing(false);
        syncFiles();
        renderPhotos();
      }
    };
    fileInput.addEventListener('change', () => addPhotos(fileInput.files));
    const resetDrag = () => { dragDepth = 0; uploadZone.classList.remove('is-dragging'); };
    uploadZone.addEventListener('dragenter', event => {
      event.preventDefault();
      if (Array.from(event.dataTransfer?.types || []).includes('Files')) { dragDepth += 1; uploadZone.classList.add('is-dragging'); }
    });
    uploadZone.addEventListener('dragover', event => {
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    });
    uploadZone.addEventListener('dragleave', event => {
      event.preventDefault();
      dragDepth = Math.max(0, dragDepth - 1);
      if (!dragDepth) uploadZone.classList.remove('is-dragging');
    });
    uploadZone.addEventListener('drop', event => {
      event.preventDefault();
      resetDrag();
      const files = event.dataTransfer?.files;
      if (!files?.length) { showPhotoError('Drop photo files from your computer, or click to choose them.'); return; }
      addPhotos(files);
    });
    const name = form.querySelector('#dog-name');
    name.addEventListener('input', () => name.setCustomValidity(name.value.trim() ? '' : 'Please enter your dog’s name.'));
    const wanted = new URLSearchParams(location.search).get('character');
    const characterOptions = Array.from(form.querySelectorAll('[name="properties[Character]"]'));
    const characterSelection = form.querySelector('[data-character-selection]');
    const chooseArtwork = (input, updatePreview = true) => {
      if (updatePreview) document.querySelector(`[data-art="${input.dataset.characterArt}"]`)?.click();
      if (characterSelection) {
        characterSelection.textContent = `Your portrait: ${input.dataset.characterLabel}. Personalized with your dog’s photo.`;
        characterSelection.hidden = false;
      }
    };
    const requestedCharacter = characterOptions.find(input => input.value === wanted);
    if (requestedCharacter) requestedCharacter.checked = true;
    characterOptions.forEach(input => {
      input.addEventListener('change', () => {
        chooseArtwork(input);
        const url = new URL(location.href);
        url.searchParams.set('character', input.value);
        history.replaceState(null, '', url);
      });
    });
    const selectedCharacter = characterOptions.find(input => input.checked);
    if (selectedCharacter) chooseArtwork(selectedCharacter, Boolean(requestedCharacter) || selectedCharacter.dataset.characterArt !== 'hero.webp');
    const variant = form.querySelector('[data-variant-select]');
    const updateFormat = (syncUrl = false) => {
      if (!variant) return;
      const selected = variant.selectedOptions[0];
      const price = document.querySelector('[data-variant-price]');
      if (price) price.textContent = selected.dataset.price;
      const formatLabel = document.querySelector('[data-selected-format]');
      if (formatLabel) formatLabel.textContent = selected.textContent.split(' — $')[0];
      form.querySelector('[data-format-title]').textContent = selected.textContent.split(' — ')[0];
      form.querySelector('[data-format-description]').textContent = 'Matte paper art print personalized with your dog’s painting, in a black wood frame with a clear protective front and hanging hardware. Approved digital portrait included.';
      const button = form.querySelector('[data-product-submit]');
      if (button && button.dataset.ordersOpen === 'true') {
        button.disabled = preparingPhotos || selected.dataset.available !== 'true';
        button.textContent = preparingPhotos ? 'Preparing your photos…' : button.disabled ? 'This format is currently unavailable' : `Add to cart — ${selected.dataset.price}`;
      }
      if (syncUrl) {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.value);
        history.replaceState(null, '', url);
      }
    };
    variant?.addEventListener('change', () => updateFormat(true));
    updateFormat();
    // Preserve Shopify's native multipart POST. Files are NOT sent as JSON or reduced to filenames.
    form.addEventListener('submit', event => {
      if (preparingPhotos) { event.preventDefault(); return; }
      name.value = name.value.trim();
      if (!name.value) name.setCustomValidity('Please enter your dog’s name.');
      if (!validatePhoto() || !form.reportValidity()) { event.preventDefault(); return; }
      const button = form.querySelector('[data-product-submit]');
      if (button.dataset.submitting === 'true') { event.preventDefault(); return; }
      button.dataset.submitting = 'true';
      button.textContent = 'Sending your patriot…';
      button.setAttribute('aria-busy', 'true');
    });
    window.addEventListener('pageshow', () => {
      const button = form.querySelector('[data-product-submit]');
      if (button?.dataset.submitting === 'true') { delete button.dataset.submitting; button.removeAttribute('aria-busy'); button.textContent = 'Add my patriot to cart ↗'; updateFormat(); }
    });
  };
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
